import { CSSProperties, FC, useEffect, useRef, useState } from 'react'

// REACH STACKER RUNNER — TOS easter egg.
// A reach stacker patrols the yard. Hop hazards, lower the boom under gantries.
//
// Controls:
//   space / ↑ / click   jump (hold for higher jump)
//   ↓                   lower boom (duck)
//   P                   pause
//   ESC                 close
//   R                   restart (after game over)
//   M                   mute
//
// CUSTOMIZE: search for `// CUSTOMIZE:` to find the bits to tailor.

// ---- world tuning ----
const GROUND_Y = 0.78
const GRAVITY = 0.9
const JUMP_VELOCITY = -14
const JUMP_HOLD_BOOST = -0.55
const JUMP_HOLD_FRAMES = 14
const BASE_SPEED = 5
const SPEED_RAMP = 0.0008
const MAX_SPEED = 13

// Stacker dimensions — chassis is fixed, only the arm moves.
const CHASSIS_W = 60
const CHASSIS_H = 30
const ARM_UP_HEIGHT = 28
const ARM_DOWN_HEIGHT = 4

// CUSTOMIZE: replace with your company name.
const COMPANY_NAME = 'TERMINAL OS'

// ---- helpers ----
function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min
}
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

interface Box {
  x: number
  y: number
  w: number
  h: number
}

function hits(a: Box, b: Box, pad = 4): boolean {
  return (
    a.x + pad < b.x + b.w && a.x + a.w - pad > b.x && a.y + pad < b.y + b.h && a.y + a.h - pad > b.y
  )
}

// ---- obstacles ----
type ObstacleType = 'pit' | 'spill' | 'worker' | 'dolly' | 'gantry' | 'chain'

interface Obstacle extends Box {
  type: ObstacleType
  walkPhase?: number
  swing?: number
}

const OBSTACLE_TYPES: ObstacleType[] = [
  'pit',
  'spill',
  'worker',
  'dolly',
  'gantry',
  'gantry',
  'chain',
]

function makeObstacle(type: ObstacleType, screenW: number, groundY: number): Obstacle | null {
  if (type === 'pit') {
    const w = rand(36, 56)
    return { type, x: screenW + 20, y: groundY - 2, w, h: 14 }
  }
  if (type === 'spill') {
    const w = rand(40, 64)
    return { type, x: screenW + 20, y: groundY - 6, w, h: 8 }
  }
  if (type === 'worker') {
    return { type, x: screenW + 20, y: groundY - 28, w: 14, h: 28, walkPhase: rand(0, Math.PI * 2) }
  }
  if (type === 'dolly') {
    return { type, x: screenW + 20, y: groundY - 26, w: 36, h: 26 }
  }
  if (type === 'gantry') {
    return { type, x: screenW + 20, y: groundY - 56, w: 42, h: 14 }
  }
  if (type === 'chain') {
    return { type, x: screenW + 20, y: groundY - 52, w: 6, h: 42, swing: 0 }
  }
  return null
}

interface Coffee extends Box {
  bob: number
}

function makeCoffee(screenW: number, groundY: number): Coffee {
  return { x: screenW + 20, y: groundY - 76 - rand(0, 24), w: 14, h: 16, bob: rand(0, Math.PI * 2) }
}

function bayLabel(meters: number): string {
  const bay = String(Math.floor(meters / 12) % 100).padStart(3, '0')
  const row = String(Math.floor(meters / 4) % 30).padStart(2, '0')
  return `BAY ${bay} · ROW ${row}`
}

// ---- AUDIO ----
type WindowWithWebkitAudio = Window & {
  webkitAudioContext?: typeof AudioContext
}

class GameAudio {
  ctx: AudioContext | null = null
  masterGain: GainNode | null = null
  muted = false

  init() {
    if (this.ctx) return
    try {
      const Ctor = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext
      if (!Ctor) return
      this.ctx = new Ctor()
      this.masterGain = this.ctx.createGain()
      this.masterGain.gain.value = 0.22
      this.masterGain.connect(this.ctx.destination)
    } catch {
      // audio unavailable — fail silently
    }
  }
  private env(node: GainNode, attack: number, decay: number, peak = 1) {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    node.gain.setValueAtTime(0, t)
    node.gain.linearRampToValueAtTime(peak, t + attack)
    node.gain.exponentialRampToValueAtTime(0.001, t + attack + decay)
  }
  jump() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'square'
    osc.frequency.setValueAtTime(220, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(440, this.ctx.currentTime + 0.1)
    this.env(gain, 0.005, 0.1, 0.18)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.12)
  }
  point() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 880
    this.env(gain, 0.005, 0.1, 0.12)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.12)
  }
  pickup() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'triangle'
    osc.frequency.setValueAtTime(660, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(1320, this.ctx.currentTime + 0.15)
    this.env(gain, 0.005, 0.18, 0.25)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.2)
  }
  duck() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const dur = 0.2
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const filter = this.ctx.createBiquadFilter()
    filter.type = 'highpass'
    filter.frequency.value = 2000
    const gain = this.ctx.createGain()
    this.env(gain, 0.005, dur, 0.15)
    src.connect(filter).connect(gain).connect(this.masterGain)
    src.start()
    src.stop(this.ctx.currentTime + dur)
  }
  crash() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const dur = 0.5
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const filter = this.ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 600
    const gain = this.ctx.createGain()
    this.env(gain, 0.005, dur, 0.6)
    src.connect(filter).connect(gain).connect(this.masterGain)
    src.start()
    src.stop(this.ctx.currentTime + dur)

    const osc = this.ctx.createOscillator()
    const gain2 = this.ctx.createGain()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(180, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(50, this.ctx.currentTime + 0.4)
    this.env(gain2, 0.005, 0.4, 0.3)
    osc.connect(gain2).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.45)
  }
  toggleMute() {
    this.muted = !this.muted
  }
}

// ---- entity types ----
interface Stacker {
  x: number
  chassisY: number
  vy: number
  onGround: boolean
  ducking: boolean
  armHeight: number
  jumpHoldFrames: number
  animFrame: number
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  color: string
  size: number
}

interface BgFar {
  x: number
  height: number
  width: number
}

interface BgCloud {
  x: number
  y: number
  w: number
  opacity: number
}

interface BgGull {
  x: number
  y: number
  flap: number
}

interface BgContainer {
  x: number
  height: number
  color: string
}

interface BgVessel {
  x: number
  width: number
  hullHeight: number
  stackHeight: number
  bridgeHeight: number
}

type GameStatus = 'ready' | 'playing' | 'paused' | 'gameover'

interface GameState {
  stacker: Stacker
  obstacles: Obstacle[]
  coffees: Coffee[]
  particles: Particle[]
  bgFar: BgFar[]
  bgClouds: BgCloud[]
  bgGulls: BgGull[]
  bgContainers: BgContainer[]
  bgVessel: BgVessel | null
  bgVesselCooldown: number
  meters: number
  hi: number
  speed: number
  status: GameStatus
  time: number
  spawnTimer: number
  coffeeTimer: number
  invincibleFrames: number
  shake: number
  milestoneAt: number
}

interface HudState {
  meters: number
  hi: number
  status: GameStatus
  muted: boolean
}

interface ReachStackerRunnerProps {
  onClose?: () => void
}

const ReachStackerRunner: FC<ReachStackerRunnerProps> = ({ onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const stateRef = useRef<GameState | null>(null)
  const keysRef = useRef<Record<string, boolean>>({})
  const audioRef = useRef<GameAudio>(new GameAudio())
  const [hud, setHud] = useState<HudState>({ meters: 0, hi: 0, status: 'ready', muted: false })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const audio = audioRef.current

    const dpr = window.devicePixelRatio || 1
    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    const W = () => canvas.width / dpr
    const H = () => canvas.height / dpr
    const groundY = () => H() * GROUND_Y

    let savedHi = 0
    try {
      savedHi = parseInt(localStorage.getItem('reachStackerHi') || '0', 10) || 0
    } catch {
      // localStorage unavailable
    }

    stateRef.current = {
      stacker: {
        x: 80,
        chassisY: groundY() - CHASSIS_H,
        vy: 0,
        onGround: true,
        ducking: false,
        armHeight: ARM_UP_HEIGHT,
        jumpHoldFrames: 0,
        animFrame: 0,
      },
      obstacles: [],
      coffees: [],
      particles: [],
      bgFar: Array.from({ length: 4 }, () => ({
        x: rand(0, W()),
        height: rand(80, 140),
        width: rand(60, 100),
      })),
      bgClouds: Array.from({ length: 4 }, () => ({
        x: Math.random() * W(),
        y: rand(20, H() * 0.35),
        w: rand(60, 120),
        opacity: rand(0.06, 0.14),
      })),
      bgGulls: Array.from({ length: 2 }, () => ({
        x: Math.random() * W(),
        y: rand(40, H() * 0.3),
        flap: Math.random() * Math.PI * 2,
      })),
      bgContainers: [],
      bgVessel: null,
      bgVesselCooldown: Math.floor(rand(120, 360)),
      meters: 0,
      hi: savedHi,
      speed: BASE_SPEED,
      status: 'ready',
      time: 0,
      spawnTimer: 60,
      coffeeTimer: 800,
      invincibleFrames: 0,
      shake: 0,
      milestoneAt: 100,
    }

    // Background container palette — dark silhouettes only, no vibrant hues.
    // Subtle hue variation to suggest individual stacks without competing
    // visually with the foreground stacker.
    const bgContainerColors = ['#222b3e', '#262e3e', '#2a3142', '#1f283a', '#2a3548', '#252e42']
    for (let i = 0; i < 10; i++) {
      stateRef.current.bgContainers.push({
        x: i * 80 + rand(-20, 20),
        height: 1 + Math.floor(Math.random() * 3),
        color: pick(bgContainerColors),
      })
    }

    const startGame = () => {
      const s = stateRef.current
      if (!s) return
      if (s.status === 'ready' || s.status === 'gameover') {
        s.status = 'playing'
        s.meters = 0
        s.speed = BASE_SPEED
        s.obstacles = []
        s.coffees = []
        s.particles = []
        s.invincibleFrames = 0
        s.spawnTimer = 60
        s.coffeeTimer = 800
        s.milestoneAt = 100
        s.stacker.chassisY = groundY() - CHASSIS_H
        s.stacker.vy = 0
        s.stacker.onGround = true
        s.stacker.armHeight = ARM_UP_HEIGHT
        setHud(p => ({ ...p, meters: 0, status: 'playing' }))
      }
    }

    const jump = () => {
      const s = stateRef.current
      if (!s) return
      if (s.status === 'ready') {
        startGame()
        return
      }
      if (s.status !== 'playing') return
      const st = s.stacker
      if (st.onGround) {
        st.vy = JUMP_VELOCITY
        st.onGround = false
        st.jumpHoldFrames = JUMP_HOLD_FRAMES
        audio.jump()
      }
    }

    const onKeyDown = (e: KeyboardEvent) => {
      audio.init()
      const wasDucking = keysRef.current['ArrowDown']
      keysRef.current[e.key] = true
      if ([' ', 'ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault()
      if (e.key === ' ' || e.key === 'ArrowUp') jump()
      const s = stateRef.current
      if (e.key === 'ArrowDown' && !wasDucking && s?.status === 'playing' && s.stacker.onGround) {
        audio.duck()
      }
      if (e.key === 'Escape' && onClose) onClose()
      if (e.key === 'p' || e.key === 'P') {
        if (!s) return
        if (s.status === 'playing') s.status = 'paused'
        else if (s.status === 'paused') s.status = 'playing'
        setHud(p => ({ ...p, status: s.status }))
      }
      if (e.key === 'm' || e.key === 'M') {
        audio.toggleMute()
        setHud(p => ({ ...p, muted: audio.muted }))
      }
      if ((e.key === 'r' || e.key === 'R') && s?.status === 'gameover') startGame()
    }
    const onKeyUp = (e: KeyboardEvent) => {
      keysRef.current[e.key] = false
    }
    const onPointerDown = () => {
      audio.init()
      jump()
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    canvas.addEventListener('pointerdown', onPointerDown)

    const explode = (x: number, y: number, color: string, count = 8) => {
      const s = stateRef.current
      if (!s) return
      for (let i = 0; i < count; i++) {
        const a = rand(-Math.PI, 0)
        const speed = rand(2, 5)
        s.particles.push({
          x,
          y,
          vx: Math.cos(a) * speed,
          vy: Math.sin(a) * speed,
          life: rand(20, 40),
          color,
          size: rand(1.5, 3),
        })
      }
    }

    let animationId = 0

    const draw = () => {
      const s = stateRef.current
      if (!s) {
        animationId = requestAnimationFrame(draw)
        return
      }
      const w = W(),
        h = H()
      const gy = groundY()
      s.time++

      let shakeX = 0
      if (s.shake > 0) {
        shakeX = rand(-s.shake, s.shake)
        s.shake *= 0.85
        if (s.shake < 0.3) s.shake = 0
      }
      ctx.setTransform(dpr, 0, 0, dpr, shakeX * dpr, 0)

      // ---- sky ----
      const sky = ctx.createLinearGradient(0, 0, 0, gy)
      sky.addColorStop(0, '#0d1622')
      sky.addColorStop(0.5, '#1a2436')
      sky.addColorStop(1, '#2a3548')
      ctx.fillStyle = sky
      ctx.fillRect(0, 0, w, gy)

      // ---- water ----
      const water = ctx.createLinearGradient(0, gy, 0, h)
      water.addColorStop(0, '#0f1a28')
      water.addColorStop(1, '#060c14')
      ctx.fillStyle = water
      ctx.fillRect(0, gy, w, h - gy)
      ctx.strokeStyle = 'rgba(80, 140, 180, 0.08)'
      for (let i = 0; i < 4; i++) {
        const y = gy + 8 + i * 14
        ctx.beginPath()
        ctx.moveTo(0, y)
        for (let x = 0; x <= w; x += 30) {
          ctx.lineTo(x, y + Math.sin((x + s.time * 1.5) * 0.025) * 2)
        }
        ctx.stroke()
      }

      // ---- distant skyline ----
      ctx.fillStyle = '#1a2236'
      s.bgFar.forEach(c => {
        if (s.status === 'playing') c.x -= s.speed * 0.05
        if (c.x + c.width < -50) {
          c.x = w + 50
          c.height = rand(80, 140)
          c.width = rand(60, 100)
        }
        const cx = c.x,
          baseY = gy - 4,
          topY = baseY - c.height
        ctx.fillRect(cx, topY, 4, c.height)
        ctx.fillRect(cx + c.width - 4, topY, 4, c.height)
        ctx.fillRect(cx - 8, topY, c.width + 16, 6)
        ctx.fillRect(cx + c.width / 2 - 8, topY + 6, 16, 10)
        ctx.fillRect(cx + 4, topY + c.height * 0.3, c.width - 8, 2)
      })

      // ---- distant container vessel (occasional) ----
      // Spawns sporadically; sits at the horizon between the cranes and the
      // mid-ground stacks. Pure silhouette so it never competes with the
      // foreground stacker for attention.
      if (s.bgVessel) {
        const v = s.bgVessel
        if (s.status === 'playing') v.x -= s.speed * 0.04
        const hullTop = gy - v.hullHeight
        const deckTop = hullTop - v.stackHeight
        const bridgeTop = hullTop - v.bridgeHeight
        ctx.fillStyle = '#1a2236'
        // Hull
        ctx.fillRect(v.x, hullTop, v.width, v.hullHeight)
        // Bow taper (right side, vessel travels left)
        ctx.beginPath()
        ctx.moveTo(v.x + v.width, hullTop)
        ctx.lineTo(v.x + v.width + 14, hullTop + v.hullHeight * 0.55)
        ctx.lineTo(v.x + v.width, hullTop + v.hullHeight)
        ctx.closePath()
        ctx.fill()
        // Solid deck mass — just one silhouette block, no individual bays
        ctx.fillRect(v.x + 16, deckTop, v.width - 22, v.stackHeight)
        // Bridge / superstructure at stern (left side)
        ctx.fillRect(v.x + 4, bridgeTop, 12, v.bridgeHeight)
        ctx.fillRect(v.x + 6, bridgeTop - 4, 8, 4)
        // Funnel
        ctx.fillRect(v.x + 8, bridgeTop - 8, 4, 5)
        // Tiny pinprick warning lights so the silhouette has a hint of life
        const lightAlpha = 0.4 + Math.sin(s.time * 0.08) * 0.3
        ctx.fillStyle = `rgba(255, 209, 102, ${lightAlpha})`
        ctx.fillRect(v.x + 9, bridgeTop - 8, 1, 1)
        ctx.fillRect(v.x + v.width - 4, hullTop + 1, 1, 1)

        if (v.x + v.width + 20 < 0) {
          s.bgVessel = null
          s.bgVesselCooldown = Math.floor(rand(600, 1400))
        }
      } else if (s.status === 'playing') {
        if (s.bgVesselCooldown > 0) s.bgVesselCooldown--
        if (s.bgVesselCooldown === 0) {
          const width = rand(180, 280)
          s.bgVessel = {
            x: w + 40,
            width,
            hullHeight: rand(14, 20),
            stackHeight: rand(12, 22),
            bridgeHeight: rand(20, 28),
          }
        }
      }

      // ---- clouds ----
      s.bgClouds.forEach(c => {
        if (s.status === 'playing') c.x -= s.speed * 0.08
        if (c.x < -c.w) {
          c.x = w + 20
          c.y = rand(20, h * 0.35)
          c.w = rand(60, 120)
          c.opacity = rand(0.06, 0.14)
        }
        ctx.fillStyle = `rgba(180, 200, 230, ${c.opacity})`
        ctx.beginPath()
        ctx.ellipse(c.x, c.y, c.w / 2, c.w / 7, 0, 0, Math.PI * 2)
        ctx.fill()
      })

      // ---- mid-ground container stacks (silhouettes) ----
      // Dark muted fills only, with a single subtle separator line. No bright
      // highlights — these are background mass, not gameplay surfaces.
      s.bgContainers.forEach(b => {
        if (s.status === 'playing') b.x -= s.speed * 0.3
        if (b.x < -60) {
          b.x = w + rand(0, 40)
          b.height = 1 + Math.floor(Math.random() * 3)
          b.color = pick(['#222b3e', '#262e3e', '#2a3142', '#1f283a', '#2a3548', '#252e42'])
        }
        const stackY = gy - 38
        for (let i = 0; i < b.height; i++) {
          ctx.fillStyle = b.color
          ctx.fillRect(b.x, stackY - i * 11, 44, 10)
          // Subtle separator between stacked containers
          ctx.fillStyle = 'rgba(0, 0, 0, 0.35)'
          ctx.fillRect(b.x, stackY - i * 11 + 9, 44, 1)
        }
      })

      // ---- seagulls ----
      ctx.strokeStyle = 'rgba(180, 195, 215, 0.35)'
      ctx.lineWidth = 1.2
      s.bgGulls.forEach(g => {
        if (s.status === 'playing') {
          g.x -= s.speed * 0.4
          g.flap += 0.15
        }
        if (g.x < -20) {
          g.x = w + 20
          g.y = rand(40, h * 0.3)
        }
        const wing = Math.sin(g.flap) * 4
        ctx.beginPath()
        ctx.moveTo(g.x - 6, g.y + wing)
        ctx.lineTo(g.x, g.y - 2)
        ctx.lineTo(g.x + 6, g.y + wing)
        ctx.stroke()
      })

      // ---- the yard ground ----
      const dockGrad = ctx.createLinearGradient(0, gy - 2, 0, gy + 16)
      dockGrad.addColorStop(0, '#3a4458')
      dockGrad.addColorStop(0.3, '#2a3445')
      dockGrad.addColorStop(1, '#1a2233')
      ctx.fillStyle = dockGrad
      ctx.fillRect(0, gy - 2, w, 18)

      ctx.save()
      ctx.translate(-((s.time * (s.status === 'playing' ? s.speed : 0)) % 24), 0)
      for (let x = 0; x < w + 24; x += 24) {
        ctx.fillStyle = '#c9a648'
        ctx.fillRect(x, gy + 14, 14, 2)
      }
      ctx.restore()

      ctx.strokeStyle = 'rgba(255, 209, 102, 0.25)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(0, gy - 2)
      ctx.lineTo(w, gy - 2)
      ctx.stroke()

      // ---- update ----
      if (s.status === 'playing') {
        s.speed = Math.min(MAX_SPEED, BASE_SPEED + s.meters * SPEED_RAMP)
        s.meters += s.speed * 0.1

        if (s.meters >= s.milestoneAt) {
          audio.point()
          s.milestoneAt += 100
        }

        const st = s.stacker
        st.animFrame++

        if (
          !st.onGround &&
          st.jumpHoldFrames > 0 &&
          (keysRef.current[' '] || keysRef.current['ArrowUp'])
        ) {
          st.vy += JUMP_HOLD_BOOST
          st.jumpHoldFrames--
        }
        st.vy += GRAVITY
        st.chassisY += st.vy

        st.ducking = !!keysRef.current['ArrowDown'] && st.onGround
        const targetArm = st.ducking ? ARM_DOWN_HEIGHT : ARM_UP_HEIGHT
        st.armHeight += (targetArm - st.armHeight) * 0.4

        if (st.chassisY + CHASSIS_H >= gy) {
          st.chassisY = gy - CHASSIS_H
          st.vy = 0
          st.onGround = true
          st.jumpHoldFrames = 0
        }

        // ---- spawning ----
        s.spawnTimer--
        if (s.spawnTimer <= 0) {
          const type = pick(OBSTACLE_TYPES)
          const ob = makeObstacle(type, w, gy)
          if (ob) s.obstacles.push(ob)
          s.spawnTimer = Math.floor(rand(60, 110) * (BASE_SPEED / s.speed))
        }
        s.coffeeTimer--
        if (s.coffeeTimer <= 0) {
          s.coffees.push(makeCoffee(w, gy))
          s.coffeeTimer = Math.floor(rand(800, 1400))
        }

        s.obstacles = s.obstacles.filter(o => {
          o.x -= s.speed
          if (o.type === 'worker' && o.walkPhase !== undefined) o.walkPhase += 0.3
          if (o.type === 'chain' && o.swing !== undefined) o.swing += 0.08
          return o.x + o.w > -10
        })
        s.coffees = s.coffees.filter(c => {
          c.x -= s.speed * 0.95
          c.bob += 0.1
          return c.x + c.w > -10
        })

        // ---- collision ----
        if (s.invincibleFrames > 0) s.invincibleFrames--
        const armTop = st.chassisY - st.armHeight
        const hb: Box = {
          x: st.x,
          y: armTop,
          w: CHASSIS_W,
          h: CHASSIS_H + st.armHeight,
        }
        for (const o of s.obstacles) {
          let oHit: Box = o
          if (o.type === 'chain' && o.swing !== undefined) {
            const sx = Math.sin(o.swing) * 8
            oHit = { x: o.x + sx, y: o.y, w: o.w, h: o.h }
          }
          if (hits(hb, oHit)) {
            if (s.invincibleFrames === 0) {
              s.status = 'gameover'
              s.shake = 16
              audio.crash()
              explode(st.x + CHASSIS_W / 2, st.chassisY + CHASSIS_H / 2, '#ffd166', 18)
              if (Math.floor(s.meters) > s.hi) {
                s.hi = Math.floor(s.meters)
                try {
                  localStorage.setItem('reachStackerHi', String(s.hi))
                } catch {
                  // localStorage unavailable
                }
              }
              setHud(p => ({ ...p, status: 'gameover', hi: s.hi }))
            }
            break
          }
        }
        for (let i = s.coffees.length - 1; i >= 0; i--) {
          if (hits(hb, s.coffees[i])) {
            s.coffees.splice(i, 1)
            s.invincibleFrames = 240
            audio.pickup()
            explode(st.x + CHASSIS_W / 2, st.chassisY, '#c97f4a', 10)
          }
        }

        s.particles = s.particles.filter(p => {
          p.x += p.vx
          p.y += p.vy
          p.vy += 0.25
          p.life--
          return p.life > 0
        })

        setHud(prev => {
          const m = Math.floor(s.meters)
          if (prev.meters === m && prev.status === s.status) return prev
          return { ...prev, meters: m, hi: s.hi, status: s.status }
        })
      }

      // ---- DRAW: obstacles ----
      s.obstacles.forEach(o => drawObstacle(ctx, o, gy, s.time))

      // ---- DRAW: coffees ----
      s.coffees.forEach(c => {
        const bobY = Math.sin(c.bob) * 3
        ctx.fillStyle = '#3a2620'
        ctx.fillRect(c.x, c.y + bobY, c.w, c.h)
        ctx.fillStyle = '#2a1b16'
        ctx.fillRect(c.x, c.y + bobY, c.w, 4)
        ctx.fillStyle = '#c97f4a'
        ctx.fillRect(c.x, c.y + bobY + 7, c.w, 5)
        ctx.strokeStyle = 'rgba(220, 230, 245, 0.5)'
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.moveTo(c.x + c.w / 2, c.y + bobY - 2)
        ctx.quadraticCurveTo(c.x + c.w / 2 + 3, c.y + bobY - 6, c.x + c.w / 2, c.y + bobY - 10)
        ctx.stroke()
        ctx.strokeStyle = `rgba(255, 209, 102, ${0.3 + Math.sin(s.time * 0.2) * 0.2})`
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.ellipse(c.x + c.w / 2, c.y + bobY + c.h / 2, c.w * 0.9, c.h * 0.7, 0, 0, Math.PI * 2)
        ctx.stroke()
      })

      // ---- DRAW: stacker ----
      drawStacker(ctx, s.stacker, s.time, s.invincibleFrames > 0, s.status === 'gameover')

      // ---- DRAW: particles ----
      s.particles.forEach(p => {
        const alpha = Math.min(1, p.life / 25)
        ctx.fillStyle = p.color
        ctx.globalAlpha = alpha
        ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size)
      })
      ctx.globalAlpha = 1

      // ---- vignette ----
      const vignette = ctx.createRadialGradient(w / 2, h / 2, h * 0.5, w / 2, h / 2, h)
      vignette.addColorStop(0, 'rgba(0,0,0,0)')
      vignette.addColorStop(1, 'rgba(0,0,0,0.5)')
      ctx.fillStyle = vignette
      ctx.fillRect(0, 0, w, h)

      animationId = requestAnimationFrame(draw)
    }
    animationId = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(animationId)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('resize', resize)
      canvas.removeEventListener('pointerdown', onPointerDown)
    }
  }, [onClose])

  return (
    <div style={styles.root}>
      <div style={styles.hud}>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>DISTANCE</span>
          <span style={styles.hudValue}>{String(hud.meters).padStart(5, '0')} M</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>BEST</span>
          <span style={styles.hudValue}>{String(hud.hi).padStart(5, '0')} M</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>POSITION</span>
          <span style={styles.hudValue}>{bayLabel(hud.meters)}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>AUDIO</span>
          <span style={styles.hudValue}>{hud.muted ? '○ MUTE' : '● ON'}</span>
        </div>
        {onClose && (
          <button style={styles.close} onClick={onClose}>
            ESC
          </button>
        )}
      </div>

      <canvas ref={canvasRef} style={styles.canvas} />

      {hud.status === 'ready' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>REACH STACKER</div>
          <div style={styles.overlaySubLarge}>tap or press SPACE to begin shift</div>
          <div style={styles.controls}>
            SPACE / ↑ jump (hold higher) &nbsp;·&nbsp; ↓ lower boom &nbsp;·&nbsp; P pause
            &nbsp;·&nbsp; M mute
          </div>
        </div>
      )}

      {hud.status === 'paused' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>HOLD POSITION</div>
          <div style={styles.overlaySub}>press P to resume</div>
        </div>
      )}

      {hud.status === 'gameover' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>STACKER DOWN</div>
          <div style={styles.overlaySub}>
            distance: {hud.meters} m {hud.meters >= hud.hi && hud.meters > 0 ? '· NEW BEST' : ''}
          </div>
          <pre style={styles.slack}>
            {`@channel reach stacker down at ${bayLabel(hud.meters).toLowerCase()}.
sending replacement. third one this shift.

— ${COMPANY_NAME} ops`}
          </pre>
          <div style={styles.overlaySub}>press R or tap to restart · ESC to close</div>
        </div>
      )}
    </div>
  )
}

export default ReachStackerRunner

// ============= STACKER =============
function drawStacker(
  ctx: CanvasRenderingContext2D,
  st: Stacker,
  time: number,
  invincible: boolean,
  dead: boolean,
) {
  const x = st.x
  const cy = st.chassisY
  const cw = CHASSIS_W
  const ch = CHASSIS_H
  const armH = st.armHeight

  if (invincible && Math.floor(time / 4) % 2 === 0) ctx.globalAlpha = 0.55

  ctx.save()
  if (dead) {
    ctx.translate(x + cw / 2, cy + ch)
    ctx.rotate(0.35)
    ctx.translate(-(x + cw / 2), -(cy + ch))
  }

  // ---- shadow ----
  ctx.fillStyle = 'rgba(0,0,0,0.35)'
  ctx.beginPath()
  ctx.ellipse(x + cw / 2, cy + ch + 4, cw / 2 + 4, 3, 0, 0, Math.PI * 2)
  ctx.fill()

  // ---- soft warm halo so the stacker pops off the dark yard ----
  const halo = ctx.createRadialGradient(x + cw / 2, cy + ch / 2, 4, x + cw / 2, cy + ch / 2, cw)
  halo.addColorStop(0, 'rgba(255, 220, 140, 0.35)')
  halo.addColorStop(1, 'rgba(255, 220, 140, 0)')
  ctx.fillStyle = halo
  ctx.fillRect(x - cw / 2, cy - cw / 2, cw * 2, cw * 2)

  // ---- chassis body with gradient (brighter) ----
  const bodyGrad = ctx.createLinearGradient(0, cy, 0, cy + ch)
  bodyGrad.addColorStop(0, '#ffd96a')
  bodyGrad.addColorStop(0.5, '#f0b840')
  bodyGrad.addColorStop(1, '#b8862e')
  ctx.fillStyle = bodyGrad
  ctx.fillRect(x + 4, cy + 6, cw - 8, ch - 14)

  // Bright outline so the silhouette reads from any background.
  ctx.strokeStyle = 'rgba(255, 240, 180, 0.85)'
  ctx.lineWidth = 1.2
  ctx.strokeRect(x + 4, cy + 6, cw - 8, ch - 14)

  ctx.fillStyle = '#3a3228'
  ctx.fillRect(x + 2, cy + ch - 8, cw - 4, 6)

  // ---- cab ----
  ctx.fillStyle = '#3a4a68'
  ctx.beginPath()
  ctx.moveTo(x + cw - 24, cy + 6)
  ctx.lineTo(x + cw - 6, cy + 6)
  ctx.lineTo(x + cw - 6, cy + 18)
  ctx.lineTo(x + cw - 28, cy + 18)
  ctx.closePath()
  ctx.fill()
  const glassGrad = ctx.createLinearGradient(0, cy + 7, 0, cy + 17)
  glassGrad.addColorStop(0, '#9bc4ee')
  glassGrad.addColorStop(1, '#5278a8')
  ctx.fillStyle = glassGrad
  ctx.fillRect(x + cw - 22, cy + 8, 14, 8)
  ctx.fillStyle = 'rgba(255,255,255,0.55)'
  ctx.fillRect(x + cw - 21, cy + 9, 4, 1)

  ctx.strokeStyle = 'rgba(0,0,0,0.4)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(x + 6, cy + 16)
  ctx.lineTo(x + cw - 30, cy + 16)
  ctx.stroke()

  // ---- the BOOM (arm) ----
  const pivotX = x + 14
  const pivotY = cy + 8
  const tipX = x + cw + 10
  const tipY = cy + 8 - armH

  ctx.save()
  ctx.lineCap = 'round'
  ctx.strokeStyle = 'rgba(255, 240, 180, 0.85)'
  ctx.lineWidth = 9
  ctx.beginPath()
  ctx.moveTo(pivotX, pivotY)
  ctx.lineTo(tipX, tipY)
  ctx.stroke()
  const armGrad = ctx.createLinearGradient(pivotX, pivotY, tipX, tipY)
  armGrad.addColorStop(0, '#ffd96a')
  armGrad.addColorStop(1, '#b8862e')
  ctx.strokeStyle = armGrad
  ctx.lineWidth = 6
  ctx.beginPath()
  ctx.moveTo(pivotX, pivotY)
  ctx.lineTo(tipX, tipY)
  ctx.stroke()
  ctx.strokeStyle = 'rgba(40, 30, 15, 0.7)'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(pivotX + 6, pivotY + 4)
  ctx.lineTo(tipX - 10, tipY + 5)
  ctx.stroke()
  ctx.fillStyle = '#1a1a1a'
  ctx.beginPath()
  ctx.arc(pivotX, pivotY, 3, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#5a5a5a'
  ctx.beginPath()
  ctx.arc(pivotX, pivotY, 1.5, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()

  // ---- spreader ----
  const spreaderW = 12,
    spreaderH = 3
  ctx.fillStyle = '#ffd96a'
  ctx.fillRect(tipX - spreaderW / 2, tipY - 1, spreaderW, spreaderH)
  ctx.strokeStyle = 'rgba(255, 240, 180, 0.85)'
  ctx.lineWidth = 1
  ctx.strokeRect(tipX - spreaderW / 2, tipY - 1, spreaderW, spreaderH)
  ctx.fillStyle = '#1a1a1a'
  ctx.fillRect(tipX - spreaderW / 2, tipY + spreaderH - 1, 2, 2)
  ctx.fillRect(tipX + spreaderW / 2 - 2, tipY + spreaderH - 1, 2, 2)

  // ---- wheels ----
  const wheelY = cy + ch - 1
  const spinAngle = (time * 0.35) % (Math.PI * 2)
  ;[x + 12, x + cw - 14].forEach(wx => {
    ctx.fillStyle = '#0d0d0d'
    ctx.beginPath()
    ctx.arc(wx, wheelY, 8, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#2a2a2a'
    ctx.beginPath()
    ctx.arc(wx, wheelY, 5, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#5a5a5a'
    ctx.beginPath()
    ctx.arc(wx, wheelY, 1.5, 0, Math.PI * 2)
    ctx.fill()
    ctx.strokeStyle = '#5a5a5a'
    ctx.lineWidth = 1.2
    ctx.beginPath()
    ctx.moveTo(wx + Math.cos(spinAngle) * 4.5, wheelY + Math.sin(spinAngle) * 4.5)
    ctx.lineTo(wx - Math.cos(spinAngle) * 4.5, wheelY - Math.sin(spinAngle) * 4.5)
    ctx.moveTo(
      wx + Math.cos(spinAngle + Math.PI / 2) * 4.5,
      wheelY + Math.sin(spinAngle + Math.PI / 2) * 4.5,
    )
    ctx.lineTo(
      wx - Math.cos(spinAngle + Math.PI / 2) * 4.5,
      wheelY - Math.sin(spinAngle + Math.PI / 2) * 4.5,
    )
    ctx.stroke()
  })

  // ---- top warning beacon ----
  const beaconAlpha = 0.5 + Math.sin(time * 0.2) * 0.5
  ctx.fillStyle = `rgba(255, 120, 120, ${beaconAlpha})`
  ctx.beginPath()
  ctx.arc(x + cw - 14, cy + 4, 2.5, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = `rgba(255, 220, 220, ${beaconAlpha})`
  ctx.beginPath()
  ctx.arc(x + cw - 14, cy + 4, 1, 0, Math.PI * 2)
  ctx.fill()

  ctx.restore()
  ctx.globalAlpha = 1
}

// ============= OBSTACLES =============
function drawObstacle(ctx: CanvasRenderingContext2D, o: Obstacle, _gy: number, _time: number) {
  if (o.type === 'pit') {
    const pitGrad = ctx.createLinearGradient(0, o.y, 0, o.y + o.h)
    pitGrad.addColorStop(0, '#000000')
    pitGrad.addColorStop(1, '#1a1a1a')
    ctx.fillStyle = pitGrad
    ctx.fillRect(o.x, o.y, o.w, o.h)
    ctx.fillStyle = 'rgba(255, 209, 102, 0.15)'
    ctx.fillRect(o.x, o.y, o.w, 2)
    ctx.fillStyle = '#c9a648'
    ctx.fillRect(o.x - 4, o.y - 2, 4, 4)
    ctx.fillRect(o.x + o.w, o.y - 2, 4, 4)
    ctx.fillStyle = '#1a1a1a'
    ctx.fillRect(o.x - 3, o.y - 1, 1, 2)
    ctx.fillRect(o.x + o.w + 2, o.y - 1, 1, 2)
    ctx.fillStyle = '#c9a648'
    ctx.fillRect(o.x + o.w + 6, o.y - 14, 2, 14)
    ctx.fillStyle = '#1a1a1a'
    ctx.fillRect(o.x + o.w + 5, o.y - 12, 4, 2)
  } else if (o.type === 'spill') {
    const grad = ctx.createRadialGradient(
      o.x + o.w / 2,
      o.y + o.h / 2,
      2,
      o.x + o.w / 2,
      o.y + o.h / 2,
      o.w / 2,
    )
    grad.addColorStop(0, '#1a0e1a')
    grad.addColorStop(0.6, '#2a1a2a')
    grad.addColorStop(1, '#0a0a14')
    ctx.fillStyle = grad
    ctx.beginPath()
    ctx.ellipse(o.x + o.w / 2, o.y + o.h / 2, o.w / 2, o.h / 2, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = 'rgba(180, 100, 200, 0.25)'
    ctx.beginPath()
    ctx.ellipse(o.x + o.w / 2 - 6, o.y + o.h / 2 - 1, o.w / 4, o.h / 5, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = 'rgba(100, 180, 200, 0.18)'
    ctx.beginPath()
    ctx.ellipse(o.x + o.w / 2 + 8, o.y + o.h / 2, o.w / 5, o.h / 6, 0, 0, Math.PI * 2)
    ctx.fill()
  } else if (o.type === 'worker') {
    const wx = o.x,
      wy = o.y
    const phase = o.walkPhase ?? 0
    const bob = Math.sin(phase) * 1.5

    ctx.fillStyle = 'rgba(0,0,0,0.3)'
    ctx.beginPath()
    ctx.ellipse(wx + 7, wy + o.h + 1, 8, 2, 0, 0, Math.PI * 2)
    ctx.fill()

    ctx.strokeStyle = '#1a2236'
    ctx.lineWidth = 2.5
    const legSwing = Math.sin(phase) * 2.5
    ctx.beginPath()
    ctx.moveTo(wx + 5, wy + 18)
    ctx.lineTo(wx + 5 - legSwing, wy + 28)
    ctx.moveTo(wx + 9, wy + 18)
    ctx.lineTo(wx + 9 + legSwing, wy + 28)
    ctx.stroke()

    ctx.fillStyle = '#d97a2c'
    ctx.fillRect(wx + 2, wy + 8 + bob * 0.3, 10, 11)
    ctx.fillStyle = 'rgba(220, 230, 245, 0.7)'
    ctx.fillRect(wx + 2, wy + 13 + bob * 0.3, 10, 1.5)

    ctx.strokeStyle = '#d97a2c'
    ctx.lineWidth = 2.5
    ctx.beginPath()
    ctx.moveTo(wx + 2, wy + 11 + bob * 0.3)
    ctx.lineTo(wx - 1, wy + 16 + bob * 0.3)
    ctx.moveTo(wx + 12, wy + 11 + bob * 0.3)
    ctx.lineTo(wx + 14, wy + 17 + bob * 0.3)
    ctx.stroke()

    ctx.fillStyle = '#e8d8a8'
    ctx.fillRect(wx + 12, wy + 16 + bob * 0.3, 4, 5)
    ctx.strokeStyle = '#5a4a2a'
    ctx.lineWidth = 0.5
    ctx.strokeRect(wx + 12, wy + 16 + bob * 0.3, 4, 5)

    ctx.fillStyle = '#d4a07a'
    ctx.beginPath()
    ctx.arc(wx + 7, wy + 5 + bob * 0.3, 3.5, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#c9a648'
    ctx.beginPath()
    ctx.arc(wx + 7, wy + 4 + bob * 0.3, 4, Math.PI, Math.PI * 2)
    ctx.fill()
    ctx.fillRect(wx + 3, wy + 4 + bob * 0.3, 8, 1)
  } else if (o.type === 'dolly') {
    const dx = o.x,
      dy = o.y
    ctx.fillStyle = 'rgba(0,0,0,0.3)'
    ctx.beginPath()
    ctx.ellipse(dx + 18, dy + o.h + 2, 22, 3, 0, 0, Math.PI * 2)
    ctx.fill()
    const ccolor = '#3a7a4a'
    ctx.fillStyle = ccolor
    ctx.fillRect(dx + 2, dy + 4, o.w - 4, o.h - 12)
    ctx.fillStyle = 'rgba(255,255,255,0.12)'
    ctx.fillRect(dx + 2, dy + 4, o.w - 4, 1)
    ctx.strokeStyle = 'rgba(0,0,0,0.3)'
    ctx.lineWidth = 0.8
    for (let i = 0; i < 5; i++) {
      const lx = dx + 4 + i * 6
      ctx.beginPath()
      ctx.moveTo(lx, dy + 5)
      ctx.lineTo(lx, dy + o.h - 9)
      ctx.stroke()
    }
    ctx.fillStyle = 'rgba(0,0,0,0.55)'
    ctx.font = '5px ui-monospace, Menlo, monospace'
    ctx.textAlign = 'center'
    ctx.fillText('EVRGU 4729103', dx + o.w / 2, dy + o.h / 2 + 1)
    ctx.fillStyle = '#3a3a3a'
    ctx.fillRect(dx, dy + o.h - 8, o.w, 4)
    ctx.fillStyle = '#0d0d0d'
    ;[dx + 6, dx + o.w - 6].forEach(wx => {
      ctx.beginPath()
      ctx.arc(wx, dy + o.h - 2, 3, 0, Math.PI * 2)
      ctx.fill()
    })
  } else if (o.type === 'gantry') {
    const beamGrad = ctx.createLinearGradient(0, o.y, 0, o.y + o.h)
    beamGrad.addColorStop(0, '#5a4520')
    beamGrad.addColorStop(0.5, '#c9a648')
    beamGrad.addColorStop(1, '#5a4520')
    ctx.fillStyle = beamGrad
    ctx.fillRect(o.x, o.y, o.w, o.h)
    ctx.fillStyle = 'rgba(20, 20, 20, 0.85)'
    for (let i = 0; i < o.w + 10; i += 7) {
      ctx.beginPath()
      ctx.moveTo(o.x + i, o.y)
      ctx.lineTo(o.x + i + 4, o.y)
      ctx.lineTo(o.x + i, o.y + o.h)
      ctx.lineTo(o.x + i - 4, o.y + o.h)
      ctx.closePath()
      ctx.fill()
    }
    ctx.fillStyle = '#3a2c12'
    ctx.fillRect(o.x - 2, o.y - 1, 2, o.h + 2)
    ctx.fillRect(o.x + o.w, o.y - 1, 2, o.h + 2)
    ctx.fillStyle = '#3a3a3a'
    ctx.fillRect(o.x + 4, 0, 4, o.y)
    ctx.fillRect(o.x + o.w - 8, 0, 4, o.y)
    ctx.strokeStyle = 'rgba(80, 80, 80, 0.6)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(o.x + 8, 20)
    ctx.lineTo(o.x + o.w - 8, o.y - 4)
    ctx.moveTo(o.x + o.w - 8, 20)
    ctx.lineTo(o.x + 8, o.y - 4)
    ctx.stroke()
  } else if (o.type === 'chain') {
    const swing = o.swing ?? 0
    const sx = Math.sin(swing) * 8
    ctx.strokeStyle = '#3a3a3a'
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.moveTo(o.x + o.w / 2, 0)
    ctx.lineTo(o.x + o.w / 2 + sx, o.y)
    ctx.stroke()
    ctx.fillStyle = '#5a5a5a'
    ctx.fillRect(o.x + sx, o.y - 2, o.w, 3)
    for (let i = 0; i < 6; i++) {
      const linkY = o.y + 3 + i * 6
      ctx.fillStyle = i % 2 === 0 ? '#7a7a7a' : '#5a5a5a'
      ctx.beginPath()
      ctx.ellipse(o.x + o.w / 2 + sx * (1 - i * 0.05), linkY, 3, 2.5, 0, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.strokeStyle = '#7a7a7a'
    ctx.lineWidth = 2
    ctx.beginPath()
    const hx = o.x + o.w / 2 + sx * 0.7
    const hy = o.y + o.h - 4
    ctx.arc(hx, hy, 3, 0, Math.PI * 1.4)
    ctx.stroke()
  }
}

const styles: Record<string, CSSProperties> = {
  root: {
    position: 'fixed',
    inset: 0,
    background: '#080e18',
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    color: '#cfd8e8',
    zIndex: 9999,
    overflow: 'hidden',
  },
  canvas: { width: '100%', height: '100%', display: 'block', touchAction: 'none' },
  hud: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '14px 22px',
    display: 'flex',
    gap: 28,
    alignItems: 'center',
    fontSize: 11,
    letterSpacing: '0.18em',
    pointerEvents: 'none',
    zIndex: 2,
    background: 'linear-gradient(to bottom, rgba(8,14,24,0.85), rgba(8,14,24,0))',
  },
  hudGroup: { display: 'flex', flexDirection: 'column', gap: 2 },
  hudLabel: { color: '#5a6478', fontSize: 9 },
  hudValue: { color: '#ffd166', fontSize: 13, letterSpacing: '0.1em' },
  close: {
    marginLeft: 'auto',
    pointerEvents: 'auto',
    background: 'transparent',
    border: '1px solid #2a3548',
    color: '#7a8398',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    background: 'rgba(8, 14, 24, 0.55)',
    zIndex: 3,
    pointerEvents: 'none',
  },
  overlayTitle: {
    fontSize: 36,
    letterSpacing: '0.32em',
    color: '#ffd166',
  },
  overlaySub: { fontSize: 12, letterSpacing: '0.18em', color: '#7a8398' },
  overlaySubLarge: { fontSize: 13, letterSpacing: '0.22em', color: '#cfd8e8', marginTop: 4 },
  controls: {
    marginTop: 24,
    fontSize: 10,
    letterSpacing: '0.18em',
    color: '#5a6478',
    textAlign: 'center',
  },
  slack: {
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid #2a3548',
    padding: '16px 22px',
    fontSize: 11,
    lineHeight: 1.7,
    color: '#cfd8e8',
    margin: 0,
    maxWidth: 460,
  },
}
